import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ContentPreview } from '@/components/ui/content-preview'
import { ContentEditor } from '@/components/ui/content-editor'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Image,
  DollarSign,
  Calendar,
  Clock,
  MapPin,
  Star,
  Award,
  TrendingUp,
  Users,
  Wrench,
  Camera,
  Copy
} from 'lucide-react'
import { supabase, ProjectRecord } from '@/lib/supabase'
import slugify from 'slugify'

const fetchProjects = async (): Promise<ProjectRecord[]> => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('updated_at', { ascending: false })

  if (error) throw error
  return data ?? []
}

type ProjectDraft = Partial<ProjectRecord> & {
  title: string
  slug: string
  description: string
  status: ProjectRecord['status']
  project_value?: number
  project_date?: string
  project_duration?: string
  client_name?: string
  content?: string
}

export default function ProjectsManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'featured' | 'archived'>('all')
  const [previewProject, setPreviewProject] = useState<ProjectRecord | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [projectDraft, setProjectDraft] = useState<ProjectDraft | null>(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)

  const queryClient = useQueryClient()
  const { data: projects = [], isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects
  })

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusColor = (status: ProjectRecord['status']) => {
    switch (status) {
      case 'published': return 'bg-chart-5/10 text-chart-5'
      case 'featured': return 'bg-accent/10 text-accent'
      case 'draft': return 'bg-muted text-muted-foreground'
      case 'archived': return 'bg-muted text-muted-foreground'
      default: return 'bg-muted text-muted-foreground'
    }
  }

  const deleteMutation = useMutation({
    mutationFn: async (project: ProjectRecord) => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', project.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    }
  })

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ProjectRecord['status'] }) => {
      const { error } = await supabase
        .from('projects')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    }
  })

  const upsertMutation = useMutation({
    mutationFn: async (payload: ProjectDraft) => {
      const baseData = {
        title: payload.title,
        slug: payload.slug,
        description: payload.description,
        status: payload.status,
        client_name: payload.client_name ?? null,
        project_value: payload.project_value ?? null,
        project_date: payload.project_date ?? null,
        project_duration: payload.project_duration ?? null,
        content: payload.content ?? '',
        updated_at: new Date().toISOString()
      }

      if (payload.id) {
        const { data, error } = await supabase
          .from('projects')
          .update(baseData)
          .eq('id', payload.id)
          .select('*')
          .single()

        if (error) throw error
        return data
      }

      const insertData = {
        ...baseData,
        created_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('projects')
        .insert([insertData])
        .select('*')
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setIsEditorOpen(false)
      setProjectDraft(null)
    }
  })

  const handleDelete = (project: ProjectRecord) => {
    if (confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      deleteMutation.mutate(project)
    }
  }

  const handleStatusChange = (id: string, newStatus: ProjectRecord['status']) => {
    statusMutation.mutate({ id, status: newStatus })
  }

  const openProjectEditor = (project?: ProjectRecord | ProjectDraft) => {
    if (project) {
      setProjectDraft({
        id: project.id,
        title: project.title,
        slug: project.slug,
        description: project.description,
        status: project.status,
        client_name: project.client_name,
        project_value: project.project_value ?? undefined,
        project_date: project.project_date ?? undefined,
        project_duration: project.project_duration ?? undefined,
        content: project.content ?? ''
      })
    } else {
      setProjectDraft({
        title: '',
        slug: '',
        description: '',
        status: 'draft',
        client_name: '',
        project_value: undefined,
        project_date: undefined,
        project_duration: '',
        content: ''
      })
    }
    setIsEditorOpen(true)
  }

  const handleEditorClose = (open: boolean) => {
    setIsEditorOpen(open)
    if (!open) {
      setProjectDraft(null)
    }
  }

  const handleEditorSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!projectDraft) return
    const slugValue = projectDraft.slug?.trim() || slugify(projectDraft.title, { lower: true, strict: true })
    upsertMutation.mutate({
      ...projectDraft,
      slug: slugValue
    })
  }

  const handleNewProject = () => openProjectEditor()
  const handleEditProject = (project: ProjectRecord) => openProjectEditor(project)
  const handleDuplicateProject = (project: ProjectRecord) => {
    openProjectEditor({
      ...project,
      id: undefined,
      title: `${project.title} (Copy)`,
      slug: `${project.slug}-copy`,
      status: 'draft'
    })
  }

  const handlePreviewProject = (project: ProjectRecord) => {
    setPreviewProject(project)
    setIsPreviewOpen(true)
  }

  const updateProjectDraft = <K extends keyof ProjectDraft>(field: K, value: ProjectDraft[K]) => {
    setProjectDraft(prev => (prev ? { ...prev, [field]: value } : prev))
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-1/2 rounded bg-muted animate-pulse" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="h-24 rounded bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-medium">Unable to load projects. Please try again later.</p>
      </div>
    )
  }

  const stats = {
    total: projects.length,
    published: projects.filter(p => p.status === 'published').length,
    featured: projects.filter(p => p.status === 'featured').length,
    draft: projects.filter(p => p.status === 'draft').length,
    totalValue: projects.reduce((sum, p) => sum + (p.project_value || 0), 0),
    avgValue: projects.length > 0 ? projects.reduce((sum, p) => sum + (p.project_value || 0), 0) / projects.length : 0
  }

  // Mock service categories for filtering
  const serviceCategories = [
    { id: '1', name: 'Brick Repair & Restoration', count: 8 },
    { id: '2', name: 'Tuckpointing', count: 5 },
    { id: '3', name: 'Chimney Repair', count: 3 },
    { id: '4', name: 'Heritage Restoration', count: 2 }
  ]

  const projectYears = [
    { year: '2024', count: 12 },
    { year: '2023', count: 18 },
    { year: '2022', count: 15 },
    { year: '2021', count: 10 }
  ]

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projects Showcase</h1>
          <p className="text-muted-foreground">
            Manage project portfolio, case studies, and client testimonials.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Camera className="mr-2 h-4 w-4" />
            Photo Gallery
          </Button>
          <Button onClick={handleNewProject}>
            <Plus className="mr-2 h-4 w-4" />
            New Project
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All project records
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.published}</div>
            <p className="text-xs text-muted-foreground">
              Live in portfolio
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Featured</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.featured}</div>
            <p className="text-xs text-muted-foreground">
              Homepage showcase
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(stats.totalValue / 1000).toFixed(0)}K
            </div>
            <p className="text-xs text-muted-foreground">
              Combined project value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(stats.avgValue / 1000).toFixed(0)}K
            </div>
            <p className="text-xs text-muted-foreground">
              Per project average
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Filters and Search */}
          <Card>
            <CardHeader>
              <CardTitle>Project Portfolio</CardTitle>
              <CardDescription>
                Manage project showcase, case studies, and client success stories.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 gap-2">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search projects..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="px-3 py-2 border border-input rounded-md bg-background text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="featured">Featured</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="mr-2 h-4 w-4" />
                    More Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Projects Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Photos</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProjects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium flex items-center gap-2">
                            {project.status === 'featured' && (
                              <Star className="h-4 w-4 text-accent fill-current" />
                            )}
                            {project.title}
                          </div>
                          <div className="text-sm text-muted-foreground line-clamp-2">
                            {project.description}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            /{project.slug}
                          </div>
                          {project.project_duration && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {project.project_duration}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={project.status}
                          onValueChange={(value) => handleStatusChange(project.id, value as ProjectRecord['status'])}
                          disabled={statusMutation.isPending}
                        >
                          <SelectTrigger className="w-[140px] capitalize">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="published">Published</SelectItem>
                            <SelectItem value="featured">Featured</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm font-medium">
                            {project.client_name || 'Private Client'}
                          </div>
                          {project.location_id && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              Toronto Area
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {project.project_date 
                            ? new Date(project.project_date).toLocaleDateString()
                            : '—'
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {project.project_value 
                            ? `$${(project.project_value / 1000).toFixed(0)}K`
                            : '—'
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <Image className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              {(project.gallery_images?.length || 0) + (project.before_images?.length || 0) + (project.after_images?.length || 0)}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreviewProject(project)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditProject(project)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDuplicateProject(project)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(project)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Service Categories */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Service Categories</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {serviceCategories.map((category) => (
                <div key={category.id} className="flex items-center justify-between">
                  <span className="text-sm">{category.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {category.count}
                  </Badge>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="w-full mt-2">
                <Plus className="mr-2 h-4 w-4" />
                Filter by Service
              </Button>
            </CardContent>
          </Card>

          {/* Project Years */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Project Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {projectYears.map((year) => (
                <div key={year.year} className="flex items-center justify-between">
                  <span className="text-sm">{year.year}</span>
                  <Badge variant="outline" className="text-xs">
                    {year.count} projects
                  </Badge>
                </div>
              ))}
              <Button variant="outline" size="sm" className="w-full mt-3">
                <Calendar className="mr-2 h-4 w-4" />
                View Timeline
              </Button>
            </CardContent>
          </Card>

          {/* Portfolio Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Portfolio Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Client Satisfaction</span>
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    <span className="font-medium">4.9/5</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Repeat Clients</span>
                  <span className="font-medium">78%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">On-Time Completion</span>
                  <span className="font-medium">95%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Referral Rate</span>
                  <span className="font-medium">85%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Portfolio Tools</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="hidden sm:block">
                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Camera className="mr-2 h-4 w-4" />
                    Photo Library
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Award className="mr-2 h-4 w-4" />
                    Testimonials
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <TrendingUp className="mr-2 h-4 w-4" />
                    Analytics
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Users className="mr-2 h-4 w-4" />
                    Client Portal
                  </Button>
                </div>
              </div>
              <div className="sm:hidden">
                <ScrollArea className="w-full">
                  <div className="flex gap-2 pb-2">
                    <Button variant="outline" size="sm" className="min-w-[200px] justify-start h-auto p-3">
                      <Camera className="mr-2 h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm font-medium leading-tight">Photo Library</div>
                        <div className="text-xs text-muted-foreground">Manage images</div>
                      </div>
                    </Button>
                    <Button variant="outline" size="sm" className="min-w-[200px] justify-start h-auto p-3">
                      <Award className="mr-2 h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm font-medium leading-tight">Testimonials</div>
                        <div className="text-xs text-muted-foreground">Update reviews</div>
                      </div>
                    </Button>
                    <Button variant="outline" size="sm" className="min-w-[200px] justify-start h-auto p-3">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm font-medium leading-tight">Analytics</div>
                        <div className="text-xs text-muted-foreground">View metrics</div>
                      </div>
                    </Button>
                    <Button variant="outline" size="sm" className="min-w-[200px] justify-start h-auto p-3">
                      <Users className="mr-2 h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm font-medium leading-tight">Client Portal</div>
                        <div className="text-xs text-muted-foreground">Share updates</div>
                      </div>
                    </Button>
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewProject?.title || 'Project Preview'}</DialogTitle>
          </DialogHeader>
          {previewProject && (
            <div className="space-y-4">
              <div className="grid gap-2 md:grid-cols-3 text-sm text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground">Client</span>
                  <div>{previewProject.client_name || 'Private Client'}</div>
                </div>
                <div>
                  <span className="font-medium text-foreground">Status</span>
                  <div className="capitalize">{previewProject.status}</div>
                </div>
                <div>
                  <span className="font-medium text-foreground">Value</span>
                  <div>{previewProject.project_value ? `$${(previewProject.project_value / 1000).toFixed(0)}K` : '—'}</div>
                </div>
              </div>
              <ContentPreview
                content={previewProject.content || previewProject.description}
                showBorder={false}
                maxHeight="none"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isEditorOpen} onOpenChange={handleEditorClose}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{projectDraft?.id ? 'Edit Project' : 'Create Project'}</DialogTitle>
            <CardDescription>Document portfolio projects with SEO-friendly content.</CardDescription>
          </DialogHeader>
          {projectDraft && (
            <form className="space-y-4" onSubmit={handleEditorSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="project-title">Title</Label>
                  <Input
                    id="project-title"
                    value={projectDraft.title}
                    onChange={(e) => updateProjectDraft('title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="project-slug">Slug</Label>
                  <Input
                    id="project-slug"
                    value={projectDraft.slug}
                    onChange={(e) => updateProjectDraft('slug', e.target.value)}
                    placeholder="historic-restoration"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label>Status</Label>
                  <Select value={projectDraft.status} onValueChange={(value) => updateProjectDraft('status', value as ProjectDraft['status'])}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="featured">Featured</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="project-date">Project Date</Label>
                  <Input
                    id="project-date"
                    type="date"
                    value={projectDraft.project_date?.split('T')[0] || ''}
                    onChange={(e) => updateProjectDraft('project_date', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="project-value">Value ($)</Label>
                  <Input
                    id="project-value"
                    type="number"
                    min="0"
                    value={projectDraft.project_value ?? ''}
                    onChange={(e) => updateProjectDraft('project_value', e.target.value ? Number(e.target.value) : undefined)}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="project-client">Client</Label>
                  <Input
                    id="project-client"
                    value={projectDraft.client_name ?? ''}
                    onChange={(e) => updateProjectDraft('client_name', e.target.value)}
                    placeholder="Heritage Properties Inc."
                  />
                </div>
                <div>
                  <Label htmlFor="project-duration">Duration</Label>
                  <Input
                    id="project-duration"
                    value={projectDraft.project_duration ?? ''}
                    onChange={(e) => updateProjectDraft('project_duration', e.target.value)}
                    placeholder="6 weeks"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="project-description">Brief Description</Label>
                <Textarea
                  id="project-description"
                  value={projectDraft.description}
                  onChange={(e) => updateProjectDraft('description', e.target.value)}
                  rows={3}
                />
              </div>

              <ContentEditor
                label="Project Content"
                value={projectDraft.content || ''}
                onChange={(content) => updateProjectDraft('content', content)}
                height={350}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => handleEditorClose(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsertMutation.isPending}>
                  {upsertMutation.isPending ? 'Saving…' : 'Save Project'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
